import { useContext, useCallback, useEffect, useMemo } from 'react';
import { Subtract } from 'utility-types';
import { SmartFormGroup } from 'components/SmartContainer/SmartContainer';
import { FieldMessage } from '../addons/FieldMessage/FieldMessage';
import { ErrorType, FormContext, IFormContext, ValidatorFunctionType } from '../models';
import { InputProps, SelectProps } from '../controls';
import { isRequiredValidator, isEmailValidator } from '../validators';

// function should return object with dependant fields values { dependantField1: value1, dependantField2: value2 }
export type UpdateDependantsFunctionType = (newValue: any, oldValues: any) => any;

export type ControlsCommonProps<P> = {
	value?: P
	onChange?(value?: P): void
	onBlur?(): void
	disabled?: boolean
}

export type FieldProps = {
	id: string
	label?: string
	isRequired?: boolean
	isEmail?: boolean
	validator?: ValidatorFunctionType
	updateDependants?: UpdateDependantsFunctionType
	disabled?: boolean
	inline?: boolean
	multiline?: boolean
	size?: 'small' | 'medium'
}

type Props = FieldProps & Subtract<(SelectProps | InputProps), ControlsCommonProps<any>> & {
	Control: any
}

export const Field = ({ id, label, isRequired, isEmail, validator, updateDependants, disabled, inline, multiline, size, Control, ...rest }: Props) => {
	const context = useContext<IFormContext>(FormContext);
	const registerValidatorsInContext = context.registerValidators; // eslint is complaining (in useEffect dependencies) when using context.registerValidators

	// register validators in FormContext, so Form has access to it
	useEffect(
		() => {
			let validatorsToRegister: ValidatorFunctionType[] = []
			isRequired && validatorsToRegister.push(isRequiredValidator);
			isEmail && validatorsToRegister.push(isEmailValidator);
			validator && validatorsToRegister.push(validator);

			registerValidatorsInContext && registerValidatorsInContext(id, validatorsToRegister)
			return () => {
				registerValidatorsInContext && registerValidatorsInContext(id, [])
			}
		},
		[id, validator, isRequired, isEmail, registerValidatorsInContext]
	)

	const onChangeCallback = useCallback(
		(value) => {
			const dependantValues = updateDependants && updateDependants(value, context.values);
			context.onFieldChange && context.onFieldChange(id, value, dependantValues);
		},
		[id, context, updateDependants]
	)

	const onBlurCallback = useCallback(
		() => context.validateField && context.validateField(id),
		[id, context]
	)

	const controlContent = useMemo(
		() => (
			<Control
				value={context.values[id]}
				onChange={onChangeCallback}
				onBlur={onBlurCallback}
				disabled={disabled || context.disabled}
				size={size}
				{...rest}
			/>
		),
		[context.disabled, context.values, onChangeCallback, onBlurCallback, disabled, id, size, rest, Control]
	)

	// Field always has ErrorType in errorsMap, so casting error as ErrorType
	const content = useMemo(
		() => {
			if (inline) {
				return (
					<>
						{controlContent}
						<FieldMessage error={context.errorsMap[id] as ErrorType} />
					</>
				);
			} else {
				return (
					<SmartFormGroup label={label} isRequired={isRequired} multiline={multiline} size={size}>
						{controlContent}
						<FieldMessage error={context.errorsMap[id] as ErrorType} />
					</SmartFormGroup>
				)
			}
		},
		[inline, controlContent, label, isRequired, id, context.errorsMap, multiline, size]
	)

	return (
		<>{content}</>
	)
}
